import React, { useMemo, useState } from "react"
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings"
import { useSelector } from "react-redux"
import { filter } from "lodash"
import moment from "moment"
import { useNavigate } from "react-router-dom"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { routes } from "router"
import IconWithBg from "ui/IconWithBg"
import { getJournalIcon, overlaps, parseTime, parseTimeOffRanges } from "pagesV2/pro/Calendar/utils"
import styles from "../index.module.css"
import { PlusCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { Button, Flex } from "antd"
import { Text } from "ui/Typography"
import TimeOffTooltip from "../TimeOffTooltip"
import { EllipsisIcon } from "icons"
import BookingItem from "../BookingItem"
import MultiDayBar from "../MultiDayBar"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import { DAY_ALERT_SLOT, MAX_VISIBLE_MONTH_EVENTS } from "pagesV2/pro/Calendar/constants"
import { Icon } from "ui/Icon"
import BlackoutReasonChip from "ui/Blackout/BlackoutReasonChip"
import BookingsActionAlert from "ui/Blackout/BookingsActionAlert"
import { getDayChipProps, hasBlackoutAction, resolveChipLabel } from "pagesV2/pro/Calendar/blackoutUtils"

function renderOverlays(timeOffRanges, allDayTimeOff) {
  return <>{!allDayTimeOff && timeOffRanges?.length ? <div aria-hidden className={styles.surfaceOverlay} /> : null}</>
}

function renderHeaderIcons({
  isMobile,
  isTablet,
  active,
  journalNoteDates,
  formattedDate,
  handleJournalModalOpen,
  handleAddBooking,
  isPastDay,
  allDayTimeOff,
  canViewJournal,
  canCreateJournal,
  canEditJournal,
  hasJournalNote,
  canCreateBooking
}) {
  if (isMobile || isTablet) return null

  const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal

  return (
    <Flex className={styles.iconHover}>
      {hasJournalPermission && (
        <IconWithBg
          disabled={!active}
          icon={getJournalIcon(journalNoteDates, formattedDate)}
          onClick={active ? () => handleJournalModalOpen(formattedDate) : undefined}
        />
      )}
      {canCreateBooking && !isPastDay && !allDayTimeOff && (
        <IconWithBg
          disabled={!active}
          icon={<PlusCircleOutlined color="var(--primary-color)" />}
          onClick={active ? handleAddBooking : undefined}
          className={styles.addIcon}
        />
      )}
    </Flex>
  )
}

/**
 * The reserved strip above a day's tiles: one row per lane the week uses, so a bar that starts
 * three columns to the left still leaves this cell's own tiles on the same line as its
 * neighbours'. Only the day a run starts on draws the bar; the rest hold an empty row.
 */
function renderSpanLanes({ spanLanes, laneCount, onOpenBooking }) {
  if (!laneCount) return null

  return (
    <div className={styles.spanLanes}>
      {Array.from({ length: laneCount }).map((_, lane) => {
        const entry = spanLanes?.[lane]
        return (
          <div key={lane} className={styles.spanLane}>
            {entry?.span > 0 && <MultiDayBar booking={entry.booking} span={entry.span} onOpen={onOpenBooking} />}
          </div>
        )
      })}
    </div>
  )
}

/**
 * The strip above the bars: the day's blackout chip and its "N bookings require action" alert.
 *
 * It leads the card so that a multi-day bar running into this day sits under the alert counting
 * it, rather than over it. Every cell in the week renders the same slots — a clear day leaves
 * them empty — because a bar hangs at the offset of the one cell its run starts in, so the rows
 * have to be reserved across the whole week or the bar cuts through them. See
 * `buildDayAlertSlots`.
 */
function renderAlertStrip({ alertSlots, blackout, formattedDate, onOpenDayPanel, t }) {
  if (!alertSlots?.length) return null
  const chip = getDayChipProps(blackout)

  return (
    <div className={styles.alertStrip}>
      {alertSlots.map((slot) => (
        <div key={slot} className={styles.alertSlot}>
          {slot === DAY_ALERT_SLOT.CHIP && chip && (
            <BlackoutReasonChip
              showCount={chip.showCount}
              label={resolveChipLabel(chip, t)}
              variant={chip.variant}
              count={chip.count}
              onClick={onOpenDayPanel ? () => onOpenDayPanel(formattedDate) : undefined}
            />
          )}
          {slot === DAY_ALERT_SLOT.ACTION && (
            <BookingsActionAlert count={blackout?.bookings_requiring_action} isCompact />
          )}
        </div>
      ))}
    </div>
  )
}

function renderEventsList({ renderList, visibleCount, onShowMore, onShowLess, isMobile, isTablet, t }) {
  if (!renderList?.length) return null
  const total = renderList.length
  const allVisible = visibleCount >= total
  const showLess = allVisible && total > MAX_VISIBLE_MONTH_EVENTS

  return (
    <Flex vertical className={`z-1 ${styles.eventsList}`} gap={4}>
      {renderList.slice(0, visibleCount)}
      {!allVisible && (
        <Button
          className={styles.viewMoreButton}
          type="link"
          onClick={onShowMore}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onShowMore()
          }}
        >
          {isMobile || isTablet ? <EllipsisIcon height={18} width={18} fill="var(--color-primary)" /> : t("calendar.view_more")}
        </Button>
      )}
      {showLess && (
        <Button
          className={styles.viewMoreButton}
          type="link"
          onClick={onShowLess}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onShowLess()
          }}
        >
          {t("calendar.view_less", "View less")}
        </Button>
      )}
    </Flex>
  )
}

/**
 * A day with something wrong with its availability — a blackout, a resource conflict, or both.
 * The date row keeps its icons; the body greys out and leads with the chip and, when bookings
 * are caught by a closure, the action alert.
 */
function renderBlackoutContent({ blackout, day, onOpenDayPanel, renderList, visibleCount, onShowMore, onShowLess, isMobile, isTablet, t, headerIconProps, spanProps, alertSlots, containerClassName }) {
  const chip = getDayChipProps(blackout)
  const formattedDate = day.format(DATE_FORMAT)
  // A full closure turns the body dark; a day that is only partly affected greys out. The
  // Legend names the two states, so they must not share one shade.
  const bodyClassName = chip?.variant === "full" ? styles.blackoutBodyFull : styles.blackoutBodyPartial

  return (
    <div className={`${containerClassName} ${styles.blackoutContainer}`}>
      <Flex justify="center" align="center" className={styles.blackoutHeader}>
        {hasBlackoutAction(blackout) && (
          <span className={styles.blackoutAlertIcon} aria-hidden>
            <Icon name="AlertTriangle" width={20} height={20} color="var(--color-error)" />
          </span>
        )}
        <Text>{day.format("DD")}</Text>
        {/* A closed day is still journalable, so the hover actions stay. */}
        {renderHeaderIcons(headerIconProps)}
      </Flex>
      <Flex vertical gap={4} className={`${styles.blackoutBody} ${bodyClassName}`}>
        {/* The alert strip, then the lane strip — in the same order in every cell of the week,
            blacked out or not. That is what keeps a bar starting columns away level with the
            space reserved for it here, and clear of this day's alert. */}
        {renderAlertStrip({ alertSlots, blackout, formattedDate, onOpenDayPanel, t })}
        {renderSpanLanes(spanProps)}
        {renderEventsList({ renderList, visibleCount, onShowMore, onShowLess, isMobile, isTablet, t })}
      </Flex>
    </div>
  )
}

function renderDayCellContent({
  timeOffRanges,
  allDayTimeOff,
  formattedDate,
  journalNoteDates,
  handleJournalModalOpen,
  handleAddBooking,
  day,
  renderList,
  visibleCount,
  onShowMore,
  onShowLess,
  onChangeFilters,
  isMobile,
  isTablet,
  active,
  isPastDay,
  t,
  canViewJournal,
  canCreateJournal,
  canEditJournal,
  hasJournalNote,
  canCreateBooking,
  blackout,
  spanLanes,
  laneCount,
  alertSlots,
  onOpenBooking,
  onOpenDayPanel
}) {
  const spanProps = { spanLanes, laneCount, onOpenBooking }
  // A bar reaches into the cells to its right, whose bodies are stacking contexts of their own at
  // the same level — so the cell a run starts in has to be raised above them.
  const startsSpan = (spanLanes || []).some((lane) => lane?.span > 0)
  const containerClassName = `${styles.timeOffContainer} ${startsSpan ? styles.spanStartCell : ""}`
  const headerIconProps = {
    isMobile,
    isTablet,
    active,
    journalNoteDates,
    formattedDate,
    handleJournalModalOpen,
    handleAddBooking,
    isPastDay,
    allDayTimeOff,
    canViewJournal,
    canCreateJournal,
    canEditJournal,
    hasJournalNote,
    canCreateBooking
  }

  // A conflict-only day is shaded like a partial one — it has an issue to surface even though
  // nothing is closed, which is what the design shows behind the "Resource Conflict" chip.
  if (blackout?.blackouts?.length || blackout?.conflicts?.length) {
    return renderBlackoutContent({
      blackout,
      day,
      onOpenDayPanel,
      renderList,
      visibleCount,
      onShowMore,
      onShowLess,
      isMobile,
      isTablet,
      t,
      headerIconProps,
      spanProps,
      alertSlots,
      containerClassName
    })
  }

  return (
    <>
      {!allDayTimeOff && timeOffRanges?.length ? (
        <TimeOffTooltip
          timeOffRanges={timeOffRanges}
          formattedDate={formattedDate}
          journalNoteDates={journalNoteDates}
          handleJournalModalOpen={handleJournalModalOpen}
          handleAddBooking={handleAddBooking}
          day={day}
          renderList={renderList}
          onChangeFilters={onChangeFilters}
        />
      ) : (
        <div className={containerClassName}>
          {renderOverlays(timeOffRanges, allDayTimeOff)}
          <Flex justify="center" align="center" className={styles.dayHeader}>
            <Text>{day.format("DD")}</Text>
            {renderHeaderIcons({
              isMobile,
              isTablet,
              active,
              journalNoteDates,
              formattedDate,
              handleJournalModalOpen,
              handleAddBooking,
              isPastDay,
              allDayTimeOff,
              canViewJournal,
              canCreateJournal,
              canEditJournal,
              hasJournalNote,
              canCreateBooking
            })}
          </Flex>
          {/* Empty here — a clear day has nothing to say — but it holds the rows the week's
              affected days need, so the bars below stay level across the row. */}
          {renderAlertStrip({ alertSlots, blackout, formattedDate, onOpenDayPanel, t })}
          {renderSpanLanes(spanProps)}
          {renderEventsList({ renderList, visibleCount, onShowMore, onShowLess, isMobile, isTablet, t })}
        </div>
      )}
    </>
  )
}

export const useDayCell = ({ day, timeOffRanges = [], allDayTimeOff = false, onChangeFilters, hideBookings = false, blackout = null, spanLanes, laneCount = 0, alertSlots = [], onOpenDayPanel }) => {
  const { bookings, journalNoteDates } = useSelector((state) => state.calendar)
  // Bookings inside a blacked-out cell are not individually flagged: the day already carries
  // the blackout chip and the "N bookings require action" alert above them. The flag only
  // drops the tile's white outline, which has nothing to separate against on a coloured body.
  const isBlackedOutDay = Boolean(blackout?.blackouts?.length)

  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [visibleCount, setVisibleCount] = useState(MAX_VISIBLE_MONTH_EVENTS)
  const onShowMore = () => setVisibleCount((c) => c + MAX_VISIBLE_MONTH_EVENTS)
  const onShowLess = () => setVisibleCount(MAX_VISIBLE_MONTH_EVENTS)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const formattedDate = day.format(DATE_FORMAT)
  const { isMobile, isTablet } = useDevice()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()

  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const hasJournalNote = journalNoteDates?.includes(formattedDate)

  const isPastDay = useMemo(() => day.isBefore(moment().startOf("day"), "day"), [day])
  const parsedTimeOff = useMemo(() => parseTimeOffRanges(timeOffRanges || []), [timeOffRanges])

  // Multi-day bookings are drawn once as a bar across the week (see `buildBookingSpans`), so the
  // per-day copies the store expands them into are left out of this day's tiles.
  const dayBookings = useMemo(() => {
    return (filter(bookings, ["date", formattedDate]) || []).filter((booking) => !booking?.multi_day)
  }, [bookings, formattedDate])

  const renderList = useMemo(() => {
    if (hideBookings) return []
    return [...dayBookings]
      .sort((a, b) => {
        const startTimeA = moment(a?.start_time, "HH:mm:ss", true)
        const startTimeB = moment(b?.start_time, "HH:mm:ss", true)
        const isValidA = startTimeA.isValid()
        const isValidB = startTimeB.isValid()
        // If both are valid, compare normally
        if (isValidA && isValidB) {
          const startTimeDiff = startTimeA.diff(startTimeB)
          if (startTimeDiff !== 0) return startTimeDiff
        } else if (isValidA) {
          return -1 // a has a valid time, b does not
        } else if (isValidB) {
          return 1 // b has a valid time, a does not
        } else {
          return 0 // neither has a valid time
        }

        // If start times are equal, compare end times
        const endTimeA = moment(a?.end_time, "HH:mm:ss", true)
        const endTimeB = moment(b?.end_time, "HH:mm:ss", true)
        const isEndValidA = endTimeA.isValid()
        const isEndValidB = endTimeB.isValid()
        if (isEndValidA && isEndValidB) {
          return endTimeA.diff(endTimeB)
        } else if (isEndValidA) {
          return -1
        } else if (isEndValidB) {
          return 1
        } else {
          return 0
        }
      })
      .map((booking) => {
        const bStart = parseTime(booking?.start_time)
        const bEnd = parseTime(booking?.end_time)
        const hasConflict = Boolean(allDayTimeOff) || parsedTimeOff.some((r) => overlaps(bStart, bEnd, r.start, r.end))
        return (
          <BookingItem
            key={booking?.id}
            booking={booking}
            conflict={hasConflict}
            isOnBlackout={isBlackedOutDay}
            setIsBookingModalOpen={setIsBookingModalOpen}
            setSelectedBookingId={setSelectedBookingId}
          />
        )
      })
  }, [dayBookings, parsedTimeOff, allDayTimeOff, hideBookings, isBlackedOutDay])

  const handleJournalModalOpen = (date) => {
    setIsJournalModalOpen(true)
    setSelectedDate(date)
  }

  const handleCancel = () => {
    setIsJournalModalOpen(false)
  }

  const handleAddBooking = () => {
    if (allDayTimeOff) return
    navigate(routes.guideBookingNewPath())
  }

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }
  const handleBookingCancelSuccess = () => {
    handleBookingCancel()
    refreshCalendarBookings()
  }

  const setJournalOpen = (open) => setIsJournalModalOpen(!!open)

  const onOpenBooking = (uuid) => {
    setIsBookingModalOpen(true)
    setSelectedBookingId(uuid)
  }

  const content = renderDayCellContent({
    timeOffRanges,
    allDayTimeOff,
    formattedDate,
    journalNoteDates,
    handleJournalModalOpen,
    handleAddBooking,
    day,
    renderList,
    visibleCount,
    onShowMore,
    onShowLess,
    onChangeFilters,
    isMobile,
    isTablet,
    active,
    isPastDay,
    t,
    canViewJournal,
    canCreateJournal,
    canEditJournal,
    hasJournalNote,
    canCreateBooking,
    blackout,
    spanLanes,
    laneCount,
    alertSlots,
    onOpenBooking,
    onOpenDayPanel
  })

  return {
    // state
    isJournalModalOpen,
    isBookingModalOpen,
    selectedDate,
    selectedBookingId,

    // derived
    formattedDate,
    isPastDay,

    // content
    content,

    // handlers
    handleCancel,
    handleBookingCancel,
    handleBookingCancelSuccess,
    refreshCalendarBookings,
    setJournalOpen
  }
}
